// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

export { AzureKeyCredential } from "@azure/core-auth";
export type {
  PagedAsyncIterableIterator,
  PageSettings,
  ContinuablePage,
} from "./static-helpers/pagingHelpers.js";
export {
  type AutocompleteItem,
  type AutocompleteMode,
  type AutocompleteResult,
  type IndexActionType,
  type IndexDocumentsResult,
  type IndexingResult,
  KnownQueryDebugMode,
  KnownSemanticErrorMode,
  KnownSemanticErrorReason,
  KnownSemanticSearchResultsType,
  KnownVectorFilterMode,
  KnownVectorQueryKind,
  type QueryDebugMode,
  type QueryResultDocumentSubscores,
  type QueryType,
  type ScoringStatistics,
  type SearchMode,
  type SingleVectorFieldResult,
  type TextResult,
  type VectorsDebugInfo,
  type SemanticSearchResultsType,
} from "./models/azure/search/documents/index.js";

export {
  type AIServices,
  type AzureBlobKnowledgeSourceParams,
  type KnowledgeBaseActivityRecord as BaseKnowledgeBaseActivityRecord,
  type KnowledgeBaseActivityRecordType,
  type KnowledgeBaseMessageContent as BaseKnowledgeBaseMessageContent,
  type KnowledgeBaseMessageContentType,
  type KnowledgeBaseReference as BaseKnowledgeBaseReference,
  type KnowledgeBaseReferenceType,
  type KnowledgeRetrievalIntentUnion as BaseKnowledgeRetrievalIntent,
  type KnowledgeRetrievalIntentType,
  type KnowledgeRetrievalReasoningEffortUnion,
  type KnowledgeRetrievalReasoningEffort as BaseKnowledgeRetrievalReasoningEffort,
  type KnowledgeRetrievalReasoningEffortKind,
  type KnowledgeSourceParams as BaseKnowledgeSourceParams,
  type KnowledgeSourceVectorizer as BaseKnowledgeSourceVectorizer,
  type IndexedOneLakeKnowledgeSourceParams,
  type KnowledgeBaseActivityRecordUnion as KnowledgeBaseActivityRecord,
  type KnowledgeBaseAgenticReasoningActivityRecord,
  type KnowledgeBaseImageContent as KnowledgeBaseMessageImageContentImage,
  type KnowledgeBaseAzureBlobReference,
  type KnowledgeBaseErrorAdditionalInfo,
  type KnowledgeBaseErrorDetail,
  type KnowledgeBaseIndexedOneLakeReference,
  type KnowledgeBaseMessage,
  type KnowledgeBaseMessageContentUnion as KnowledgeBaseMessageContent,
  type KnowledgeBaseMessageImageContent,
  type KnowledgeBaseMessageTextContent,
  type KnowledgeBaseReferenceUnion as KnowledgeBaseReference,
  type KnowledgeBaseRetrievalRequest,
  type KnowledgeBaseRetrievalResponse,
  type KnowledgeSourceStatus,
  type SynchronizationState,
  type KnowledgeSourceSynchronizationError,
  type CompletedSynchronizationState,
  type KnowledgeSourceStatistics,
  type KnowledgeBaseSearchIndexReference,
  type KnowledgeBaseWebReference,
  type KnowledgeRetrievalIntent,
  type KnowledgeRetrievalMinimalReasoningEffort,
  type KnowledgeRetrievalSemanticIntent,
  type KnowledgeSourceParamsUnion as KnowledgeSourceParams,
  type SearchIndexKnowledgeSourceParams,
  type WebKnowledgeSourceParams,
} from "./models/azure/search/documents/knowledgeBases/index.js";
export {
  type AIFoundryModelCatalogName,
  type AIServicesAccountKey,
  type BM25Similarity,
  type AnalyzedTokenInfo,
  type AnalyzeResult,
  type AsciiFoldingTokenFilter,
  type AzureActiveDirectoryApplicationCredentials,
  type AzureOpenAIModelName,
  type CharFilter as BaseCharFilter,
  type CognitiveServicesAccount as BaseCognitiveServicesAccount,
  type DataChangeDetectionPolicy as BaseDataChangeDetectionPolicy,
  type DataDeletionDetectionPolicy as BaseDataDeletionDetectionPolicy,
  type KnowledgeBaseModel as BaseKnowledgeBaseModel,
  type LexicalAnalyzer as BaseLexicalAnalyzer,
  type LexicalNormalizer as BaseLexicalNormalizer,
  type LexicalTokenizer as BaseLexicalTokenizer,
  type ScoringFunction as BaseScoringFunction,
  type SearchIndexerDataIdentity as BaseSearchIndexerDataIdentity,
  type SearchIndexerSkill as BaseSearchIndexerSkill,
  type TokenFilter as BaseTokenFilter,
  type VectorSearchCompression as BaseVectorSearchCompression,
  type BinaryQuantizationCompression,
  type CharFilterName,
  type ChatCompletionExtraParametersBehavior,
  type ChatCompletionResponseFormat,
  type ChatCompletionSchemaProperties as ChatCompletionResponseFormatJsonSchemaProperties,
  type ChatCompletionResponseFormatType,
  type ChatCompletionSchema,
  type CjkBigramTokenFilter,
  type CjkBigramTokenFilterScripts,
  type ClassicSimilarity,
  type ClassicTokenizer,
  type CognitiveServicesAccountKey,
  type CommonGramTokenFilter,
  type CommonModelParameters,
  type ConditionalSkill,
  type ContentUnderstandingSkill,
  type ContentUnderstandingSkillChunkingProperties,
  type ContentUnderstandingSkillChunkingUnit,
  type ContentUnderstandingSkillExtractionOptions,
  KnownContentUnderstandingSkillChunkingUnit,
  KnownContentUnderstandingSkillExtractionOptions,
  type CorsOptions,
  type CustomEntity,
  type CustomEntityAlias,
  type CustomNormalizer as CustomLexicalNormalizer,
  type DefaultCognitiveServicesAccount,
  type DictionaryDecompounderTokenFilter,
  type DistanceScoringFunction,
  type DistanceScoringParameters,
  type DocumentExtractionSkill,
  type DocumentIntelligenceLayoutSkillChunkingProperties,
  type DocumentIntelligenceLayoutSkillChunkingUnit,
  type DocumentIntelligenceLayoutSkillExtractionOptions,
  type DocumentIntelligenceLayoutSkillMarkdownHeaderDepth,
  type DocumentIntelligenceLayoutSkillOutputFormat,
  type DocumentIntelligenceLayoutSkillOutputMode,
  type EdgeNGramTokenFilterSide,
  type EdgeNGramTokenizer,
  type ElisionTokenFilter,
  type EntityLinkingSkill,
  type EntityRecognitionSkillV3,
  type EntityCategory,
  type EntityRecognitionSkillLanguage,
  type FieldMapping,
  type FieldMappingFunction,
  type FreshnessScoringFunction,
  type FreshnessScoringParameters,
  type HighWaterMarkChangeDetectionPolicy,
  type IndexerExecutionResult,
  type IndexerExecutionStatus,
  type IndexerResyncOption,
  type IndexerStatus,
  type IndexingSchedule,
  type IndexProjectionMode,
  type InputFieldMappingEntry,
  type KeepTokenFilter,
  type KeywordMarkerTokenFilter,
  type KnowledgeBaseModelKind,
  type KnowledgeSourceContentExtractionMode,
  type KnowledgeSourceKind,
  type KnowledgeSourceReference,
  type KnowledgeSourceSynchronizationStatus,
  KnownAIFoundryModelCatalogName,
  KnownAzureOpenAIModelName,
  KnownBlobIndexerDataToExtract,
  KnownBlobIndexerImageAction,
  KnownBlobIndexerParsingMode,
  KnownBlobIndexerPDFTextRotationAlgorithm,
  KnownChatCompletionExtraParametersBehavior,
  KnownChatCompletionResponseFormatType,
  KnownCustomEntityLookupSkillLanguage,
  KnownDocumentIntelligenceLayoutSkillChunkingUnit,
  KnownDocumentIntelligenceLayoutSkillExtractionOptions,
  KnownDocumentIntelligenceLayoutSkillMarkdownHeaderDepth,
  KnownDocumentIntelligenceLayoutSkillOutputFormat,
  KnownDocumentIntelligenceLayoutSkillOutputMode,
  KnownEntityCategory,
  KnownEntityRecognitionSkillLanguage,
  KnownImageAnalysisSkillLanguage,
  KnownImageDetail,
  KnownIndexerExecutionEnvironment,
  KnownIndexerResyncOption,
  KnownIndexProjectionMode,
  KnownKeyPhraseExtractionSkillLanguage,
  KnownKnowledgeBaseModelKind,
  KnownKnowledgeSourceKind,
  KnownLexicalAnalyzerName,
  KnownLexicalNormalizerName,
  KnownMarkdownHeaderDepth,
  KnownMarkdownParsingSubmode,
  KnownLexicalNormalizerName as KnownNormalizerNames,
  KnownOcrLineEnding,
  KnownOcrSkillLanguage,
  KnownPIIDetectionSkillMaskingMode,
  KnownRankingOrder,
  KnownRegexFlags,
  KnownSearchFieldDataType,
  KnownSearchIndexerDataSourceType,
  KnownSentimentSkillLanguage,
  KnownSplitSkillLanguage,
  KnownTextSplitMode,
  KnownTextTranslationSkillLanguage,
  KnownVectorEncodingFormat,
  KnownVectorSearchAlgorithmKind,
  KnownVectorSearchAlgorithmMetric,
  KnownVectorSearchCompressionKind,
  KnownVectorSearchCompressionRescoreStorageMethod,
  KnownVectorSearchCompressionTarget,
  KnownVectorSearchVectorizerKind,
  KnownVisualFeature,
  type LanguageDetectionSkill,
  type LengthTokenFilter,
  type LexicalAnalyzerName,
  type LexicalNormalizerName,
  type LexicalTokenizerName,
  type LimitTokenFilter,
  // ListIndexStatsSummary,
  type LuceneStandardAnalyzer,
  type MagnitudeScoringFunction,
  type MagnitudeScoringParameters,
  type MappingCharFilter,
  type MarkdownHeaderDepth,
  type MarkdownParsingSubmode,
  type MergeSkill,
  type MicrosoftLanguageStemmingTokenizer,
  type MicrosoftLanguageTokenizer,
  type MicrosoftStemmingTokenizerLanguage,
  type MicrosoftTokenizerLanguage,
  type NativeBlobSoftDeleteDeletionDetectionPolicy,
  type NGramTokenizer,
  type OcrLineEnding,
  type OutputFieldMappingEntry,
  type PathHierarchyTokenizerV2 as PathHierarchyTokenizer,
  type PatternCaptureTokenFilter,
  type PatternReplaceCharFilter,
  type PatternReplaceTokenFilter,
  type PhoneticEncoder,
  type PhoneticTokenFilter,
  type RankingOrder,
  type RescoringOptions,
  type ResourceCounter,
  type ScalarQuantizationCompression,
  type ScalarQuantizationParameters,
  type ScoringFunctionAggregation,
  type ScoringFunctionInterpolation,
  type SearchAlias,
  type SearchIndexerDataContainer,
  type SearchIndexerDataNoneIdentity,
  type SearchIndexerDataUserAssignedIdentity,
  type SearchIndexerError,
  type SearchIndexerIndexProjectionSelector,
  type SearchIndexerKnowledgeStoreBlobProjectionSelector,
  type SearchIndexerKnowledgeStoreFileProjectionSelector,
  type SearchIndexerKnowledgeStoreObjectProjectionSelector,
  type SearchIndexerKnowledgeStoreProjection,
  type SearchIndexerKnowledgeStoreProjectionSelector,
  type SearchIndexerKnowledgeStoreTableProjectionSelector,
  type SearchIndexerLimits,
  type SearchIndexerStatus,
  type SearchIndexerWarning,
  type SearchIndexFieldReference,
  type SearchIndexKnowledgeSourceParameters,
  type SearchSuggester as Suggester,
  type SemanticConfiguration,
  type SemanticField,
  type SemanticPrioritizedFields,
  type SemanticSearch,
  type SentimentSkillV3,
  type SentimentSkillLanguage,
  type ServiceCounters,
  type ServiceLimits,
  type ShaperSkill,
  type ShingleTokenFilter,
  type SimilarityAlgorithmUnion as Similarity,
  type SimilarityAlgorithm as BaseSimilarityAlgorithm,
  type SnowballTokenFilter,
  type SnowballTokenFilterLanguage,
  type SoftDeleteColumnDeletionDetectionPolicy,
  type SqlIntegratedChangeTrackingPolicy,
  type StemmerOverrideTokenFilter,
  type StemmerTokenFilter,
  type StemmerTokenFilterLanguage,
  type StopAnalyzer,
  type StopwordsList,
  type StopwordsTokenFilter,
  // SynchronizationState,
  type SynonymTokenFilter,
  type TagScoringFunction,
  type TagScoringParameters,
  type TextWeights,
  type TokenCharacterKind,
  type TokenFilterName,
  type TruncateTokenFilter,
  type UaxUrlEmailTokenizer,
  type UniqueTokenFilter,
  type VectorEncodingFormat,
  type VectorSearchCompressionKind,
  type VectorSearchCompressionRescoreStorageMethod,
  type VectorSearchCompressionTarget,
  type VectorSearchProfile,
  type VectorSearchVectorizerKind,
  type WebKnowledgeSourceDomains,
  type WebKnowledgeSourceParameters,
  type WebKnowledgeSourceDomain,
  type WordDelimiterTokenFilter,
} from "./models/azure/search/documents/indexes/index.js";
export type { KnowledgeBaseModelWebSummarizationActivityRecord } from "./models/index.js";
export { default as GeographyPoint } from "./geographyPoint.js";
export { IndexDocumentsBatch } from "./indexDocumentsBatch.js";
export type {
  AutocompleteOptions,
  AutocompleteRequest,
  BaseSearchRequestOptions,
  BaseVectorQuery,
  CountDocumentsOptions,
  DeleteDocumentsOptions,
  DocumentDebugInfo,
  ExcludedODataTypes,
  ExtractDocumentKey,
  ExtractiveQueryAnswer,
  ExtractiveQueryCaption,
  GenerativeQueryRewrites,
  GetDocumentOptions,
  IndexDocumentsAction,
  IndexDocumentsOptions,
  ListSearchResultsPageSettings,
  MergeDocumentsOptions,
  MergeOrUploadDocumentsOptions,
  NarrowedModel,
  QueryAnswer,
  QueryCaption,
  QueryResultDocumentSemanticField,
  QueryRewrites,
  SearchDocumentsPageResult,
  SearchDocumentsResult,
  SearchDocumentsResultBase,
  SearchFieldArray,
  SearchIndexingBufferedSenderDeleteDocumentsOptions,
  SearchIndexingBufferedSenderFlushDocumentsOptions,
  SearchIndexingBufferedSenderMergeDocumentsOptions,
  SearchIndexingBufferedSenderMergeOrUploadDocumentsOptions,
  SearchIndexingBufferedSenderOptions,
  SearchIndexingBufferedSenderUploadDocumentsOptions,
  SearchIterator,
  SearchOptions,
  SearchPick,
  SearchRequestOptions,
  SearchRequestQueryTypeOptions,
  SearchResult,
  SelectFields,
  SemanticDebugInfo,
  SemanticErrorMode,
  SemanticErrorReason,
  SemanticSearchOptions,
  SuggestDocumentsResult,
  SuggestNarrowedModel,
  SuggestOptions,
  SuggestRequest,
  SuggestResult,
  UnionToIntersection,
  UploadDocumentsOptions,
  VectorFilterMode,
  VectorizableImageBinaryQuery,
  VectorizableImageUrlQuery,
  VectorizableTextQuery,
  VectorizedQuery,
  VectorQuery,
  VectorQueryKind,
  VectorSearchOptions,
} from "./indexModels.js";
export type { KnowledgeBase, RetrieveOptions } from "./knowledgeBaseModels.js";
export {
  KnowledgeRetrievalClient,
  type KnowledgeRetrievalClientOptions,
} from "./knowledgeRetrievalClient.js";
export { odata } from "./odata.js";
export { KnownSearchAudience } from "./searchAudience.js";
export { SearchClient, type SearchClientOptions } from "./searchClient.js";
export { SearchIndexClient, type SearchIndexClientOptions } from "./searchIndexClient.js";
export { SearchIndexerClient, type SearchIndexerClientOptions } from "./searchIndexerClient.js";
export {
  DEFAULT_BATCH_SIZE,
  DEFAULT_FLUSH_WINDOW,
  DEFAULT_RETRY_COUNT,
  type IndexDocumentsClient,
  SearchIndexingBufferedSender,
} from "./searchIndexingBufferedSender.js";
export {
  type AIServicesAccountIdentity,
  type AliasIterator,
  type AnalyzeRequest,
  type AnalyzeTextOptions,
  type AzureBlobKnowledgeSource,
  type AzureBlobKnowledgeSourceParameters,
  type AzureMachineLearningVectorizer,
  type AzureMachineLearningVectorizerParameters,
  type AzureOpenAIEmbeddingSkill,
  type AzureOpenAIParameters,
  type AzureOpenAIVectorizer,
  type BaseAzureMachineLearningVectorizerParameters,
  type BaseKnowledgeSource,
  type BaseVectorSearchAlgorithmConfiguration,
  type BaseVectorSearchVectorizer,
  type BlobIndexerDataToExtract,
  type BlobIndexerImageAction,
  type BlobIndexerParsingMode,
  type BlobIndexerPDFTextRotationAlgorithm,
  type CharFilter,
  type ChatCompletionSkill,
  type CognitiveServicesAccount,
  type ComplexDataType,
  type ComplexField,
  type CreateAliasOptions,
  type CreateDataSourceConnectionOptions,
  type CreateIndexerOptions,
  type CreateIndexOptions,
  type CreateKnowledgeBaseOptions,
  type CreateKnowledgeSourceOptions,
  type CreateOrUpdateAliasOptions,
  type CreateorUpdateDataSourceConnectionOptions,
  type CreateorUpdateIndexerOptions,
  type CreateOrUpdateIndexOptions,
  type CreateOrUpdateKnowledgeBaseOptions,
  type CreateOrUpdateKnowledgeSourceOptions,
  type CreateOrUpdateSkillsetOptions,
  type CreateOrUpdateSynonymMapOptions,
  type CreateSkillsetOptions,
  type CreateSynonymMapOptions,
  type CustomAnalyzer,
  type CustomEntityLookupSkill,
  type CustomEntityLookupSkillLanguage,
  type DataChangeDetectionPolicy,
  type DataDeletionDetectionPolicy,
  type DeleteAliasOptions,
  type DeleteDataSourceConnectionOptions,
  type DeleteIndexerOptions,
  type DeleteIndexOptions,
  type DeleteKnowledgeBaseOptions,
  type DeleteKnowledgeSourceOptions,
  type DeleteSkillsetOptions,
  type DeleteSynonymMapOptions,
  type DocumentIntelligenceLayoutSkill,
  type EdgeNGramTokenFilter,
  type EntityRecognitionSkill,
  type ExhaustiveKnnAlgorithmConfiguration,
  type ExhaustiveKnnParameters,
  type FacetResult,
  type GetAliasOptions,
  type GetDataSourceConnectionOptions,
  type GetIndexerOptions,
  type GetIndexerStatusOptions,
  type GetIndexOptions,
  type GetIndexStatisticsOptions,
  type GetKnowledgeBaseOptions,
  type GetKnowledgeSourceOptions,
  type GetKnowledgeSourceStatusOptions,
  type GetServiceStatisticsOptions,
  type GetSkillSetOptions,
  type GetSynonymMapsOptions,
  type HnswAlgorithmConfiguration,
  type HnswParameters,
  type ImageAnalysisSkill,
  type ImageAnalysisSkillLanguage,
  type ImageDetail,
  type IndexedOneLakeKnowledgeSource,
  type IndexedOneLakeKnowledgeSourceParameters,
  type IndexerExecutionEnvironment,
  type IndexingParameters,
  type IndexingParametersConfiguration,
  type IndexIterator,
  type IndexNameIterator,
  type KeyAuthAzureMachineLearningVectorizerParameters,
  type KeyPhraseExtractionSkill,
  type KeyPhraseExtractionSkillLanguage,
  type KeywordTokenizer,
  type KnowledgeBaseAzureOpenAIModel,
  type KnowledgeBaseIterator,
  type KnowledgeBaseModel,
  type KnowledgeSource,
  type KnowledgeSourceAzureOpenAIVectorizer,
  type KnowledgeSourceIngestionParameters,
  type KnowledgeSourceIterator,
  type KnowledgeSourceVectorizer,
  KnownAnalyzerNames,
  KnownCharFilterNames,
  KnownTokenFilterNames,
  KnownTokenizerNames,
  type LexicalAnalyzer,
  type LexicalNormalizer,
  type LexicalTokenizer,
  type ListAliasesOptions,
  type ListDataSourceConnectionsOptions,
  type ListIndexersOptions,
  type ListIndexesOptions,
  type ListKnowledgeBasesOptions,
  type ListKnowledgeSourcesOptions,
  type ListSkillsetsOptions,
  type ListSynonymMapsOptions,
  type LuceneStandardTokenizer,
  type NGramTokenFilter,
  type NoAuthAzureMachineLearningVectorizerParameters,
  type OcrSkill,
  type OcrSkillLanguage,
  type PatternAnalyzer,
  type PatternTokenizer,
  type PIIDetectionSkill,
  type PIIDetectionSkillMaskingMode,
  type QueryAnswerResult,
  type QueryCaptionResult,
  type RegexFlags,
  type ResetIndexerOptions,
  type RunIndexerOptions,
  type ScoringFunction,
  type ScoringProfile,
  type SearchField,
  type SearchFieldDataType,
  type SearchIndex,
  type SearchIndexAlias,
  type SearchIndexer,
  type SearchIndexerDataIdentity,
  type SearchIndexerDataSourceConnection,
  type SearchIndexerDataSourceType,
  type SearchIndexerIndexProjection,
  type SearchIndexerIndexProjectionParameters,
  type SearchIndexerKnowledgeStore,
  type SearchIndexerKnowledgeStoreParameters,
  type SearchIndexerSkill,
  type SearchIndexerSkillset,
  type SearchIndexKnowledgeSource,
  type SearchIndexStatistics,
  type SearchResourceEncryptionKey,
  type SearchServiceStatistics,
  type SentimentSkill,
  type SimilarityAlgorithm,
  type SimpleField,
  type SplitSkill,
  type SplitSkillLanguage,
  type SynonymMap,
  type TextSplitMode,
  type TextTranslationSkill,
  type TextTranslationSkillLanguage,
  type TokenAuthAzureMachineLearningVectorizerParameters,
  type TokenFilter,
  type VectorSearch,
  type VectorSearchAlgorithmConfiguration,
  type VectorSearchAlgorithmKind,
  type VectorSearchAlgorithmMetric,
  type VectorSearchCompression,
  type VectorSearchVectorizer,
  type VisualFeature,
  type WebApiParameters,
  type WebApiSkill,
  type WebApiSkills,
  type WebApiVectorizer,
  type WebKnowledgeSource,
} from "./serviceModels.js";
export { createSynonymMapFromFile } from "#platform/synonymMapHelper";
